import React, { useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { Toast } from "components/shared/Toaster";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  CircularProgress,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Input from "components/forms/Input";
import Button from "../../../../shared/Button";
import DropdownField from "components/forms/ReactHookForm/DropdownField";
import { Alert } from "@material-ui/lab";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";

const SEND_FAILED_FALLBACK = "Sending to DocuSign failed. Please try again.";

/**
 * DocuSign errors arrive double wrapped: the API message is a string that contains DocuSign's own JSON,
 * e.g. `400 Bad Request: "{"errorCode":"INVALID_EMAIL_ADDRESS_FOR_RECIPIENT","message":"..."}"`.
 * This digs out the inner message so the tooltip shows something readable instead of the raw envelope.
 */
const parseDocusignError = (rawMessage) => {
  if (!rawMessage) return SEND_FAILED_FALLBACK;

  const start = rawMessage.indexOf("{");
  const end = rawMessage.lastIndexOf("}");
  if (start === -1 || end <= start) return rawMessage;

  try {
    const inner = JSON.parse(rawMessage.slice(start, end + 1).replace(/\\"/g, '"'));
    if (inner?.errorCode === "INVALID_EMAIL_ADDRESS_FOR_RECIPIENT") {
      return "DocuSign rejected the envelope because a signer has no email address. Set it in Settings > Notifications before sending.";
    }
    return inner?.message || rawMessage;
  } catch (e) {
    return rawMessage;
  }
};

/** Reads the rejected fetch Response (silent requests leave the body unread) and formats its message. */
const readSendError = async (error) => {
  try {
    const body = await error?.json?.();
    const rawMessage = Array.isArray(body)
      ? body.map((item) => item?.message).filter(Boolean).join(" ")
      : body?.message;
    return parseDocusignError(rawMessage);
  } catch (e) {
    return SEND_FAILED_FALLBACK;
  }
};

const hasFactoringFeesValues = (fees = {}) =>
  [
    fees?.factoringFeeFirstPeriod,
    fees?.initialPeriod,
    fees?.incrementPerSubsequentPeriod,
    fees?.subsequentPeriodLength,
  ].some((value) => Number(value) > 0);

/**
 * Saved terms turned into form state. Lives outside the component so both the load effect and the
 * "discard changes" action rebuild exactly the same values from the same source.
 */
const formStateFromTermData = (termData) => {
  const baseTerms = termData?.baseTerms || {};
  const factoringFees = termData?.factoringFees || {};
  const hasFactoringFees = hasFactoringFeesValues(factoringFees);

  return {
    hasFactoringFees,
    useFactorCloudTerms: !!termData?.useFactorCloudTerms,
    values: {
      escrowReserveRate: baseTerms?.escrowReserveRate != null ? Number((baseTerms.escrowReserveRate * 100).toFixed(2)) : "",
      purchaseRate: baseTerms?.purchaseRate != null ? Number((baseTerms.purchaseRate * 100).toFixed(2)) : "",
      advanceRate: baseTerms?.advanceRate != null ? Number((baseTerms.advanceRate * 100).toFixed(2)) : "",

      recoursePeriod: baseTerms?.recoursePeriod ?? "",
      dueDiligenceFee: baseTerms?.dueDiligenceFee ?? "",
      minimumPurchaseFee: baseTerms?.minimumPurchaseFee ?? "",
      feePerInvoice: baseTerms?.feePerInvoice ?? "",
      lateCharge: baseTerms?.lateCharge ?? "",
      termAutoRenew: baseTerms?.termAutoRenew ?? "",

      factoringFeeFirstPeriod: hasFactoringFees && factoringFees?.factoringFeeFirstPeriod != null ? Number((factoringFees.factoringFeeFirstPeriod * 100).toFixed(2)) : "",
      initialPeriod: hasFactoringFees && factoringFees?.initialPeriod != null ? factoringFees.initialPeriod : "",
      incrementPerSubsequentPeriod: hasFactoringFees && factoringFees?.incrementPerSubsequentPeriod != null ? Number((factoringFees.incrementPerSubsequentPeriod * 100).toFixed(2)) : "",
      subsequentPeriodLength: hasFactoringFees && factoringFees?.subsequentPeriodLength != null ? factoringFees.subsequentPeriodLength : "",

      applicationTermsId: termData?.applicationTermsId || "",
      applicationDocusignTemplateId: termData?.applicationDocusignTemplateId || "",
    },
  };
};

const useStyles = makeStyles((theme) => ({
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  topDivider: {
    marginBottom: theme.spacing(3),
  },
  container: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  inputField: {
    marginBottom: theme.spacing(2),
  },
  inputWrapper: {
    position: "relative",
  },
  prefixIcon: {
    position: "absolute",
    left: "12px",
    top: "40%",
    transform: "translateY(-50%)",
    fontSize: "15px",
    fontFamily: "inherit",
    color: "#333",
    pointerEvents: "none",
  },
  suffixIcon: {
    position: "absolute",
    right: "12px",
    top: "40%",
    transform: "translateY(-50%)",
    fontSize: "15px",
    fontFamily: "inherit",
    color: "#333",
    pointerEvents: "none",
  },
  inputWithPrefix: {
    "& input": {
      paddingLeft: "24px",
    },
  },
  inputWithSuffixSmall: {
    "& input": {
      paddingRight: "30px",
    },
  },
  inputWithSuffixMedium: {
    "& input": {
      paddingRight: "45px",
    },
  },
  inputWithSuffixLarge: {
    "& input": {
      paddingRight: "55px",
    },
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 3),
  },
  dialogButton: {
    whiteSpace: "nowrap",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

// Shown when the application reads its terms from FactorCloud but the fetch failed. The rates on screen are
// the local fallback, so saving or sending them would write the wrong values into a signed agreement.
const FACTOR_CLOUD_UNAVAILABLE_MESSAGE =
  "FactorCloud terms could not be loaded, so the values shown are not the FactorCloud ones. Reload the tab before saving or sending.";

const DOCUSIGN_ACCESS_EXPIRED_MESSAGE =
  "DocuSign access expired, so its templates could not be loaded. Reconnect the account in Settings > Docusign before sending.";

const TermsForm = ({
  termData = {},
  fCTermsDetail = {},
  loadingFCTermDetail,
  onSubmit,
  sendToDocusign,
  formLoader,
  fetchTermsData,
  fetchTermsDataByTermId,
  termsList,
  templatesList,
  docusignAccessExpired,
}) => {
  const classes = useStyles();

  // Helper to safely convert input values to numbers or keep original if invalid
  const toNumber = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    return isNaN(n) ? v : n;
  };

  const toOptionalPercent = (v) => (v === "" || v === null || v === undefined ? null : Number(v) / 100);
  const toOptionalInteger = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));

  /**
   * Enhanced Validation Rules Generator
   * Includes support for 'customValidations' to handle cross-field checks.
   */
  const getNumericRules = (label, { min = 0, max = null, isInteger = false, required = true, customValidations = {} } = {}) => ({
    required: required ? `${label} is required` : false,
    validate: {
      isNumber: (v) => {
        if (!required && (v === "" || v === null)) return true;
        return (v !== "" && !isNaN(Number(v)) && Number.isFinite(Number(v))) || `${label} must be a valid number`;
      },
      min: (v) => {
        if (!required && (v === "" || v === null)) return true;
        return Number(v) >= min || `${label} must be greater than or equal to ${min}`;
      },
      max: (v) => {
        if (!required && (v === "" || v === null)) return true;
        if (max !== null && Number(v) > max) return `${label} cannot exceed ${max}`; // Changed message for context
        return true;
      },
      isInteger: (v) => {
        if (!required && (v === "" || v === null)) return true;
        if (isInteger && !Number.isInteger(Number(v))) return `${label} must be a whole number`;
        return true;
      },
      ...customValidations // Inject custom logic (like sum checks)
    }
  });

  const { control, handleSubmit, reset, watch, setValue, trigger, clearErrors, formState: { isDirty } } = useForm({
    defaultValues: {
      applicationTermsId: "",
      applicationDocusignTemplateId: "",
      escrowReserveRate: "",
      purchaseRate: "",
      advanceRate: "",
      lateCharge: "",
      dueDiligenceFee: "",
      minimumPurchaseFee: "",
      feePerInvoice: "",
      recoursePeriod: "",
      termAutoRenew: "",
      factoringFeeFirstPeriod: "",
      initialPeriod: "",
      incrementPerSubsequentPeriod: "",
      subsequentPeriodLength: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [docusignEnvelopeStatus, setDocusignEnvelopeStatus] = useState(undefined);
  const [createTerm, setCreateTerm] = useState(false);
  const [useFactoringFees, setUseFactoringFees] = useState(false);
  const [alreadySent, setAlreadySent] = useState(true);
  const [termsNotSaved, setTermsNotSaved] = useState(true);
  const [disableSubmit, setDisableSubmit] = useState(false);
  // Whether this client has FactorCloud available, and the per-application choice to use it. When FactorCloud
  // is off, terms are entered manually in ROX (the Factor Cloud Terms select and "Create New Term" are hidden).
  const [useFactorCloud, setUseFactorCloud] = useState(false);
  // Toggle state as last saved, so an unsaved toggle flip counts as a pending change (the switches are
  // local state, not form fields, so react-hook-form's isDirty does not see them).
  const [savedToggles, setSavedToggles] = useState({ useFactoringFees: false, useFactorCloud: false });
  const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false);
  // Last DocuSign failure, kept so the user can hover SEND DOCUSIGN and read why it did not go out.
  const [sendError, setSendError] = useState(null);
  // Availability comes from the backend, which decides it with the credentials the terms endpoints actually
  // need. The securityContext flag is stricter (it also requires the onboarding subdomain), so relying on it
  // here would hide the toggle for clients the backend still reads terms from, and saving would then switch
  // them to local terms behind the user's back.
  const factorCloudAvailable = !!termData?.factorCloudAvailable;
  const factorCloudMode = factorCloudAvailable && useFactorCloud;
  // Set to use FactorCloud but the fetch failed: the rates below are the local fallback, not the FactorCloud
  // ones, so saving them would overwrite the real terms. Block writes until the tab loads cleanly.
  const factorCloudTermsUnavailable = !!termData?.factorCloudTermsUnavailable;
  // Local terms with rates that were never stored (rows predating local rates). They read as 0, so they can be
  // corrected and saved, but must not be sent as-is.
  const localTermsIncomplete = !!termData?.localTermsIncomplete;
  // In local mode the rate fields are always editable; in FactorCloud mode only while creating a new term.
  const ratesDisabled = factorCloudMode && !createTerm;

  const createTermFieldRules = (label, options = {}) =>
    getNumericRules(label, { ...options, required: !ratesDisabled && options.required !== false });

  // Watch values for calculation and cross-validation
  const escrowReserveRate = watch("escrowReserveRate");
  const purchaseRate = watch("purchaseRate");
  const factoringFeeFirstPeriod = watch("factoringFeeFirstPeriod");
  const initialPeriod = watch("initialPeriod");
  const incrementPerSubsequentPeriod = watch("incrementPerSubsequentPeriod");
  const subsequentPeriodLength = watch("subsequentPeriodLength");
  const factoringFeesStarted = [
    factoringFeeFirstPeriod,
    initialPeriod,
    incrementPerSubsequentPeriod,
    subsequentPeriodLength,
  ].some((value) => value !== "" && value !== null && value !== undefined);

  // Effect: Calculate Advance Rate & Trigger validation when rates change
  React.useEffect(() => {
    const escrow = Number(escrowReserveRate) || 0;
    const purchase = Number(purchaseRate) || 0;

    const calculatedAdvanceRate = 100 - escrow - purchase;
    const finalAdvanceRate = Math.max(0, calculatedAdvanceRate);

    setValue("advanceRate", Number(finalAdvanceRate.toFixed(2)), {
      shouldValidate: !ratesDisabled,
      shouldDirty: !ratesDisabled,
    });

    // Trigger validation to clear/show errors if the sum changes
    if (!ratesDisabled && escrowReserveRate !== "" && purchaseRate !== "") {
        trigger(["escrowReserveRate", "purchaseRate"]);
    }

  }, [ratesDisabled, escrowReserveRate, purchaseRate, setValue, trigger]);

  React.useEffect(() => {
    if (termData) {
      const { values, hasFactoringFees, useFactorCloudTerms } = formStateFromTermData(termData);

      setAlreadySent(termData.hasDocusignEnvelope);
      setTermsNotSaved(!(termData.termsSaved || termData.applicationTermsId));
      setDocusignEnvelopeStatus(termData.docusignEnvelopeStatus);
      setUseFactoringFees(hasFactoringFees);
      setUseFactorCloud(useFactorCloudTerms);
      setSavedToggles({ useFactoringFees: hasFactoringFees, useFactorCloud: useFactorCloudTerms });

      reset(values);
    }
  }, [termData, reset]);

  React.useEffect(() => {
    if (fCTermsDetail) {
        const shouldValidate = createTerm;
        const hasFactoringFees = hasFactoringFeesValues(fCTermsDetail);
        setUseFactoringFees(hasFactoringFees);
        setValue("escrowReserveRate", fCTermsDetail.escrowReserveRate ? Number((fCTermsDetail.escrowReserveRate * 100).toFixed(2)) : "", { shouldValidate });
        setValue("purchaseRate", fCTermsDetail.purchaseRate ? Number((fCTermsDetail.purchaseRate * 100).toFixed(2)) : "", { shouldValidate });
        setValue("advanceRate", fCTermsDetail.advanceRate ? Number((fCTermsDetail.advanceRate * 100).toFixed(2)) : "", { shouldValidate });
        // Amounts, not rates: no *100, and != null so a legitimate 0 is not blanked out.
        setValue("minimumPurchaseFee", fCTermsDetail.minimumPurchaseFee != null ? Number(fCTermsDetail.minimumPurchaseFee) : "", { shouldValidate });
        setValue("feePerInvoice", fCTermsDetail.feePerInvoice != null ? Number(fCTermsDetail.feePerInvoice) : "", { shouldValidate });
        setValue("factoringFeeFirstPeriod", hasFactoringFees && fCTermsDetail.factoringFeeFirstPeriod != null ? Number((fCTermsDetail.factoringFeeFirstPeriod * 100).toFixed(2)) : "", { shouldValidate });
        setValue("initialPeriod", hasFactoringFees && fCTermsDetail.initialPeriod != null ? Number(fCTermsDetail.initialPeriod) : "", { shouldValidate });
        setValue("incrementPerSubsequentPeriod", hasFactoringFees && fCTermsDetail.incrementPerSubsequentPeriod != null ? Number((fCTermsDetail.incrementPerSubsequentPeriod * 100).toFixed(2)) : "", { shouldValidate });
        setValue("subsequentPeriodLength", hasFactoringFees && fCTermsDetail.subsequentPeriodLength != null ? Number(fCTermsDetail.subsequentPeriodLength) : "", { shouldValidate });
    }
  }, [createTerm, fCTermsDetail, setValue]);

  React.useEffect(() => {
    if (ratesDisabled) {
      clearErrors([
        "escrowReserveRate",
        "purchaseRate",
        "advanceRate",
        "factoringFeeFirstPeriod",
        "initialPeriod",
        "incrementPerSubsequentPeriod",
        "subsequentPeriodLength",
      ]);
    }
  }, [clearErrors, ratesDisabled]);

  React.useEffect(() => {
    if (!useFactoringFees || !factoringFeesStarted) {
      clearErrors([
        "factoringFeeFirstPeriod",
        "initialPeriod",
        "incrementPerSubsequentPeriod",
        "subsequentPeriodLength",
      ]);
    }
  }, [clearErrors, factoringFeesStarted, useFactoringFees]);

  // Clear the fee fields only when the toggle actually goes on -> off, never on mount: the form mounts with
  // termData already loaded, so an effect that clears whenever the flag is false runs once with its initial
  // false and wipes the values reset() just wrote. Covering the transition (instead of the state) also keeps
  // the invariant when the flag is turned off programmatically by a termData/fCTermsDetail load.
  const factoringFeesEnabledRef = React.useRef(useFactoringFees);
  React.useEffect(() => {
    const wasEnabled = factoringFeesEnabledRef.current;
    factoringFeesEnabledRef.current = useFactoringFees;

    if (wasEnabled && !useFactoringFees) {
      setValue("factoringFeeFirstPeriod", "", { shouldValidate: false, shouldDirty: true });
      setValue("initialPeriod", "", { shouldValidate: false, shouldDirty: true });
      setValue("incrementPerSubsequentPeriod", "", { shouldValidate: false, shouldDirty: true });
      setValue("subsequentPeriodLength", "", { shouldValidate: false, shouldDirty: true });
    }
  }, [setValue, useFactoringFees]);

  const onFormSubmit = (data) => {
    const factoringFeesPayload = useFactoringFees
      ? {
          factoringFeeFirstPeriod: toOptionalPercent(data.factoringFeeFirstPeriod),
          initialPeriod: toOptionalInteger(data.initialPeriod),
          incrementPerSubsequentPeriod: toOptionalPercent(data.incrementPerSubsequentPeriod),
          subsequentPeriodLength: toOptionalInteger(data.subsequentPeriodLength),
        }
      : null;

    const formattedData = {
      baseTerms: {
        escrowReserveRate: Number(data.escrowReserveRate) / 100,
        purchaseRate: Number(data.purchaseRate) / 100,
        advanceRate: Number(data.advanceRate) / 100,
        recoursePeriod: parseInt(data.recoursePeriod, 10),
        dueDiligenceFee: Number(data.dueDiligenceFee),
        minimumPurchaseFee: Number(data.minimumPurchaseFee),
        feePerInvoice: Number(data.feePerInvoice),
        lateCharge: Number(data.lateCharge),
        termAutoRenew: parseInt(data.termAutoRenew, 10),
      },
      factoringFees: factoringFeesPayload,
      applicationTermsId: (factorCloudMode && !createTerm) ? data.applicationTermsId : undefined,
      applicationDocusignTemplateId: data.applicationDocusignTemplateId,
      useFactorCloudTerms: factorCloudMode,
    };

    return onSubmit && onSubmit(formattedData);
  };

  // The switches live outside the form, so both sources have to be checked.
  const hasUnsavedChanges =
    isDirty ||
    useFactoringFees !== savedToggles.useFactoringFees ||
    useFactorCloud !== savedToggles.useFactorCloud;

  const runSendToDocusign = () => {
    setDisableSubmit(true);
    setSendError(null);
    return sendToDocusign()
      .then(() => {
        Toast({ type: "success", message: "Document has been sent successfully!" });
        fetchTermsData();
      })
      .catch(async (error) => {
        const message = await readSendError(error);
        setSendError(message);
        Toast({ type: "error", message });
      })
      .finally(() => {
        setDisableSubmit(false);
      });
  };

  const handleSendClick = () => {
    if (hasUnsavedChanges) {
      setUnsavedChangesOpen(true);
      return;
    }
    runSendToDocusign();
  };

  // Saves first and only sends if the save went through, so the envelope never goes out with terms that
  // failed to persist. The dialog closes before validating so validation errors are visible inline.
  const handleSaveAndSend = () => {
    setUnsavedChangesOpen(false);
    handleSubmit(async (data) => {
      const saved = await onFormSubmit(data);
      if (saved === false) return;
      await runSendToDocusign();
    })();
  };

  const handleDiscardAndSend = () => {
    setUnsavedChangesOpen(false);
    const { values, hasFactoringFees, useFactorCloudTerms } = formStateFromTermData(termData);
    setUseFactoringFees(hasFactoringFees);
    setUseFactorCloud(useFactorCloudTerms);
    reset(values);
    runSendToDocusign();
  };

  /**
   * Custom Validator: Sum Check
   * Ensures that (Escrow + Purchase) <= 100
   */
  const validateTotalRate = (value, otherValueName) => {
    const otherVal = Number(watch(otherValueName)) || 0;
    const currentVal = Number(value) || 0;
    if (currentVal + otherVal > 100) {
        return "Total rates (Escrow + Purchase) cannot exceed 100%";
    }
    return true;
  };

  const validateFactoringFeesGroup = (value, label) => {
    if (ratesDisabled || !useFactoringFees) {
      return true;
    }

    if (value === "" || value === null || value === undefined) {
      return `${label} is required`;
    }

    return true;
  };

  return (
    <Box>
      <div className={classes.titleContainer} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
        <Typography variant="h5" className={classes.title} style={{ marginRight: "auto" }}>
          Terms
        </Typography>

        <Chip
          label={"Docusign Envelope Status: " + (docusignEnvelopeStatus ? docusignEnvelopeStatus : "Not Sent")}
          color={"default"}
        />

        <Tooltip
          title={
            alreadySent
              ? "Terms cannot be modified once submitted to DocuSign."
              : factorCloudTermsUnavailable
              ? FACTOR_CLOUD_UNAVAILABLE_MESSAGE
              : ""
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSubmit(onFormSubmit)}
              disabled={formLoader || alreadySent || factorCloudTermsUnavailable}
              loading={formLoader}
            >
              SAVE TERMS
            </Button>
          </span>
        </Tooltip>

        <Tooltip
          title={
            alreadySent
              ? "The envelope has already been sent to Docusign."
              : factorCloudTermsUnavailable
              ? FACTOR_CLOUD_UNAVAILABLE_MESSAGE
              : termsNotSaved
              ? "You must save the terms to be able to send to Docusign."
              : localTermsIncomplete
              ? "Some rates were never set for this application. Review them and save before sending to Docusign."
              : docusignAccessExpired
              ? DOCUSIGN_ACCESS_EXPIRED_MESSAGE
              : sendError || ""
          }
          arrow
        >
          <span>
            <Button
              loading={disableSubmit}
              variant="contained"
              color={"secondary"}
              onClick={handleSendClick}
              disabled={alreadySent || termsNotSaved || disableSubmit || factorCloudTermsUnavailable || localTermsIncomplete || docusignAccessExpired}
            >
              SEND DOCUSIGN
            </Button>
          </span>
        </Tooltip>
      </div>

      <Divider className={classes.topDivider} />

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Paper className={classes.container} elevation={0}>
          {/* ... (Select Terms And Template Section - same as before) ... */}
           <Typography variant="h6" className={classes.sectionTitle}>Select Terms And Template</Typography>

          {factorCloudAvailable && (
            <Box mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useFactorCloud}
                    onChange={(e) => setUseFactorCloud(e.target.checked)}
                    color="primary"
                    disabled={alreadySent}
                  />
                }
                label="Use Factor Cloud terms"
              />
            </Box>
          )}

          <Alert severity="info" className="flex-fill mr-2" style={{marginBottom: '20px'}}>
            {factorCloudMode
              ? "Before continuing, please select the Factor Cloud Terms and the DocuSign template."
              : "Enter the terms below and select the DocuSign template."}
          </Alert>

          {docusignAccessExpired && (
            <Alert severity="warning" className="flex-fill mr-2" style={{marginBottom: '20px'}}>
              {DOCUSIGN_ACCESS_EXPIRED_MESSAGE}
            </Alert>
          )}

          <Grid container spacing={3}>
            {factorCloudMode && (
            <Grid item xs={6}>
              <Grid container spacing={2} alignItems="center" wrap="nowrap">
                <Grid item xs>
                  {termsList && (
                    <Controller
                      name="applicationTermsId"
                      control={control}
                      rules={{ required: !createTerm ? "Factor Cloud Terms are required" : false }}
                      render={({ field, fieldState }) => (
                        <DropdownField
                          {...field}
                          control={control}
                          label="Factor Cloud Terms"
                          placeholder="Select Factor Cloud Terms"
                          options={termsList.map((term) => ({ label: term.name, value: term.id }))}
                          isSearchable
                          error={fieldState.error?.message}
                          onChange={(val) => {
                            field.onChange(val);
                            if (val != null) fetchTermsDataByTermId(val);
                          }}
                          disabled={createTerm}
                        />
                      )}
                    />
                  )}
                </Grid>
                <Grid item style={{ flexShrink: 0 }}>
                    <div style={{ marginTop: "22px" }}>
                        <FormControlLabel
                            control={<Switch onChange={(e) => setCreateTerm(e.target.checked)} color="primary" />}
                            label="Create New Term"
                        />
                    </div>
                </Grid>
              </Grid>
            </Grid>
            )}
            <Grid item xs={12} sm={6}>
              {templatesList && (
                <Controller
                  name="applicationDocusignTemplateId"
                  control={control}
                  rules={{ required: "Template On Docusign is required" }}
                  render={({ field, fieldState }) => (
                    <DropdownField
                      {...field}
                      control={control}
                      label="Template On Docusign"
                      placeholder="Select Template On Docusign"
                      options={templatesList.map((t) => ({ label: t.name, value: t.templateId }))}
                      isSearchable
                      error={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.container} elevation={0}>
          <Typography variant="h6" className={classes.sectionTitle}>Rox Base Terms</Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="lateCharge"
                control={control}
                rules={getNumericRules("Late Charge", { min: 0, max: 100 })}
                render={({ field, fieldState }) => (
                  <div className={classes.inputWrapper}>
                    <Input
                      {...field}
                      onChange={(e) => field.onChange(toNumber(e.target.value))}
                      label="Late Charge (% per day after recourse)"
                      type="number"
                      shrink
                      className={`${classes.inputField} ${classes.inputWithSuffixSmall}`}
                      error={fieldState.error?.message}
                      description="Daily late charge after recourse period"
                      inputProps={{ step: "0.01", min: "0", max: "100" }}
                    />
                    <span className={classes.suffixIcon}>%</span>
                  </div>
                )}
              />
            </Grid>

            {/* Recourse Period & Term Auto Renew - Integers */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="recoursePeriod"
                control={control}
                rules={getNumericRules("Recourse Period", { min: 0, isInteger: true })}
                render={({ field, fieldState }) => (
                  <div className={classes.inputWrapper}>
                    <Input
                      {...field}
                      onChange={(e) => field.onChange(toNumber(e.target.value))}
                      label="Recourse Period"
                      type="number"
                      shrink
                      className={`${classes.inputField} ${classes.inputWithSuffixMedium}`}
                      error={fieldState.error?.message}
                      description="Period before recourse to client"
                      inputProps={{ step: "1", min: "0" }}
                    />
                    <span className={classes.suffixIcon}>days</span>
                  </div>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="termAutoRenew"
                control={control}
                rules={getNumericRules("Term (auto-renew)", { min: 0, isInteger: true })}
                render={({ field, fieldState }) => (
                  <div className={classes.inputWrapper}>
                    <Input
                      {...field}
                      onChange={(e) => field.onChange(toNumber(e.target.value))}
                      label="Term (auto-renew)"
                      type="number"
                      shrink
                      className={`${classes.inputField} ${classes.inputWithSuffixLarge}`}
                      error={fieldState.error?.message}
                      description="Initial contract term length"
                      inputProps={{ step: "1", min: "0" }}
                    />
                    <span className={classes.suffixIcon}>months</span>
                  </div>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="dueDiligenceFee"
                control={control}
                rules={getNumericRules("Due Diligence Fee", { min: 0 })}
                render={({ field, fieldState }) => (
                  <div className={classes.inputWrapper}>
                    <Input
                      {...field}
                      onChange={(e) => field.onChange(toNumber(e.target.value))}
                      label="Due Diligence Fee"
                      type="number"
                      shrink
                      className={`${classes.inputField} ${classes.inputWithPrefix}`}
                      error={fieldState.error?.message}
                      description="One-time setup and verification fee"
                      inputProps={{ step: "0.01", min: "0" }}
                    />
                    <span className={classes.prefixIcon}>$</span>
                  </div>
                )}
              />
            </Grid>

          </Grid>

          <Divider className={classes.divider} />

          {/* Only name FactorCloud when the values actually come from it; entered locally they are just rates. */}
          <Typography variant="h6" className={classes.sectionTitle}>
            {factorCloudMode ? "Factor Cloud Terms" : "Rates and Fees"}
          </Typography>

          {loadingFCTermDetail ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <CircularProgress size={48} color="primary" />
            </div>
          ) : (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="escrowReserveRate"
                    control={control}
                    // Validates Max 100 AND Sum Check with Purchase Rate
                    rules={createTermFieldRules("Escrow Reserve Rate", {
                        min: 0, 
                        max: 100,
                        customValidations: {
                            sumCheck: (v) => validateTotalRate(v, "purchaseRate")
                        }
                    })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Escrow Reserve Rate"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithSuffixSmall}`}
                          error={fieldState.error?.message}
                          description="Percentage of Escrow Reserve Rate"
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                          disabled={ratesDisabled}
                        />
                        <span className={classes.suffixIcon}>%</span>
                      </div>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="purchaseRate"
                    control={control}
                    // Validates Max 100 AND Sum Check with Escrow Rate
                    rules={createTermFieldRules("Purchase Rate", {
                        min: 0, 
                        max: 100,
                        customValidations: {
                            sumCheck: (v) => validateTotalRate(v, "escrowReserveRate")
                        }
                    })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Purchase Rate"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithSuffixSmall}`}
                          error={fieldState.error?.message}
                          description="Percentage of Purchase Rate"
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                          disabled={ratesDisabled}
                        />
                        <span className={classes.suffixIcon}>%</span>
                      </div>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="advanceRate"
                    control={control}
                    // Read only, validation handled by source fields
                    render={({ field }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          label="Advance Rate"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithSuffixSmall}`}
                          description="Advance Rate (Calculated)"
                          disabled
                        />
                        <span className={classes.suffixIcon}>%</span>
                      </div>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="minimumPurchaseFee"
                    control={control}
                    rules={createTermFieldRules("Minimum Purchase Fee", { min: 0 })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Minimum Purchase Fee"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithPrefix}`}
                          error={fieldState.error?.message}
                          description="Minimum fee charged per purchase"
                          inputProps={{ step: "0.01", min: "0" }}
                          disabled={ratesDisabled}
                        />
                        <span className={classes.prefixIcon}>$</span>
                      </div>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="feePerInvoice"
                    control={control}
                    rules={createTermFieldRules("Fee per Invoice", { min: 0 })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Fee per Invoice"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithPrefix}`}
                          error={fieldState.error?.message}
                          description="Flat fee charged per invoice"
                          inputProps={{ step: "0.01", min: "0" }}
                          disabled={ratesDisabled}
                        />
                        <span className={classes.prefixIcon}>$</span>
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" className={classes.sectionTitle}>Factoring Fees</Typography>

              <Box mb={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useFactoringFees}
                      onChange={(e) => setUseFactoringFees(e.target.checked)}
                      color="primary"
                      disabled={ratesDisabled}
                    />
                  }
                  label="Include Factoring Fees"
                />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="factoringFeeFirstPeriod"
                    control={control}
                    // Validates Max 100
                    rules={createTermFieldRules("Factoring Fee (First Period)", {
                      min: 0,
                      max: 100,
                      required: false,
                      customValidations: {
                        groupRequired: (v) => validateFactoringFeesGroup(v, "Factoring Fee (First Period)"),
                      },
                    })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Factoring Fee (First Period)"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithSuffixSmall}`}
                          error={fieldState.error?.message}
                          description="Percentage fee for the initial period"
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                          disabled={ratesDisabled || !useFactoringFees}
                        />
                        <span className={classes.suffixIcon}>%</span>
                      </div>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="initialPeriod"
                    control={control}
                    rules={createTermFieldRules("Initial Period", {
                      min: 0,
                      isInteger: true,
                      required: false,
                      customValidations: {
                        groupRequired: (v) => validateFactoringFeesGroup(v, "Initial Period"),
                      },
                    })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Initial Period"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithSuffixMedium}`}
                          error={fieldState.error?.message}
                          description="Length of initial fee period"
                          inputProps={{ step: "1", min: "0" }}
                          disabled={ratesDisabled || !useFactoringFees}
                        />
                        <span className={classes.suffixIcon}>days</span>
                      </div>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="incrementPerSubsequentPeriod"
                    control={control}
                     // Validates Max 100
                    rules={createTermFieldRules("Increment per Subsequent Period", {
                      min: 0,
                      max: 100,
                      required: false,
                      customValidations: {
                        groupRequired: (v) => validateFactoringFeesGroup(v, "Increment per Subsequent Period"),
                      },
                    })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Increment per Subsequent Period"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithSuffixSmall}`}
                          error={fieldState.error?.message}
                          description="Additional fee per subsequent period"
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                          disabled={ratesDisabled || !useFactoringFees}
                        />
                        <span className={classes.suffixIcon}>%</span>
                      </div>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="subsequentPeriodLength"
                    control={control}
                    rules={createTermFieldRules("Subsequent Period Length", {
                      min: 0,
                      isInteger: true,
                      required: false,
                      customValidations: {
                        groupRequired: (v) => validateFactoringFeesGroup(v, "Subsequent Period Length"),
                      },
                    })}
                    render={({ field, fieldState }) => (
                      <div className={classes.inputWrapper}>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(toNumber(e.target.value))}
                          label="Subsequent Period Length"
                          type="number"
                          shrink
                          className={`${classes.inputField} ${classes.inputWithSuffixMedium}`}
                          error={fieldState.error?.message}
                          description="Length of each subsequent period"
                          inputProps={{ step: "1", min: "0" }}
                          disabled={ratesDisabled || !useFactoringFees}
                        />
                        <span className={classes.suffixIcon}>days</span>
                      </div>
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      </form>

      <Dialog open={unsavedChangesOpen} onClose={() => setUnsavedChangesOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have changes to the terms that have not been saved. DocuSign will receive the last saved
            terms unless you save them first.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button variant="text" className={classes.dialogButton} onClick={() => setUnsavedChangesOpen(false)}>
            Cancel
          </Button>
          {/* A save or send already in flight would otherwise be duplicated: SEND DOCUSIGN stays
              clickable while formLoader is true, so the dialog can be opened mid-save. */}
          <Button
            variant="outlined"
            className={classes.dialogButton}
            onClick={handleDiscardAndSend}
            disabled={formLoader || disableSubmit}
          >
            Discard and send
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.dialogButton}
            onClick={handleSaveAndSend}
            disabled={formLoader || disableSubmit}
          >
            Save and send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ... propTypes (same as before) ...
TermsForm.propTypes = {
  termData: PropTypes.shape({
    advanceRate: PropTypes.number,
    id: PropTypes.string,
    name: PropTypes.string,
    active: PropTypes.bool,
    escrowReserveRate: PropTypes.number,
    purchaseRate: PropTypes.number,
    feePerInvoice: PropTypes.number,
    minimumPurchaseFee: PropTypes.number,
    baseCollectionFeesOn: PropTypes.string,
    invoiceAmount: PropTypes.string,
    escrow: PropTypes.string,
    collectionFees: PropTypes.array,
    additionalFeesInfo: PropTypes.array,
    editable: PropTypes.bool,
    nOpenInvoices: PropTypes.number,
    collectionFeesMinimum: PropTypes.number,
    collectionFeesCalculateAgeBasedOn: PropTypes.string,
    collectionFeesCountsAsFirstDay: PropTypes.bool,
    collectionFeesFloatDays: PropTypes.number,
    hasDocusignEnvelope: PropTypes.bool,
    docusignEnvelopeStatus: PropTypes.string,
    applicationTermsId: PropTypes.string,
    applicationDocusignTemplateId: PropTypes.string,
    baseTerms: PropTypes.object,
    factoringFees: PropTypes.object,
  }),
  fCTermsDetail: PropTypes.object,
  loadingFCTermDetail: PropTypes.bool,
  onSubmit: PropTypes.func,
  sendToDocusign: PropTypes.func,
  formLoader: PropTypes.bool,
  fetchTermsData: PropTypes.func,
  fetchTermsDataByTermId: PropTypes.func,
  termsList: PropTypes.array,
  templatesList: PropTypes.array,
  docusignAccessExpired: PropTypes.bool,
};

export default TermsForm;
